import { MouseEvent, useState } from 'react';
import {
  APPLE_AUTH_ENABLED,
  GOOGLE_AUTH_ENABLED,
  MSAL_ENABLED,
  PUBLIC_KEY_PEM,
} from '../../constants/global.constants';
import { zenworksServiceApi } from '../../service/zenworksService.api';
import ButtonWithLoading from '../common/ButtomWIthLoading';
// import ReCAPTCHA from 'react-google-recaptcha';
import ShowPasswordInput from '../common/ShowPasswordInput';
import DisplayInfoMessage from '../common/DisplayInfoMessage';
import {
  isValidEmail,
  isValidPassword,
} from '../../utils/validations';
import { encryptMessageWithRSAOAEP } from '../../utils/encryption';
import { generateId } from '../../utils/functions';
// import Login from './Login';

interface SignUpProps {
  handelScreen: (screenName: string) => void;
  handelSignUp: (userData: any) => void;
  title?: string;
}
const SignUp = ({ handelSignUp, title }: SignUpProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const userEmail = localStorage.getItem('userEmail') || '';
  const [form, setForm] = useState({
    email: userEmail,
    fullName: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    confirmPassword: '',
    tAndC: true,
    ComplianceUpdates: false,
    captcha: 'test',
  });
  const [displayMessage, setDisplayMessage] = useState({
    error: false,
    message: '',
  });

  const handelDisplayMessage = ({ error = true, message }: any) => {
    setDisplayMessage({ error, message });
    setTimeout(() => {
      setDisplayMessage({
        error: false,
        message: '',
      });
    }, 5000);
  };

  const handleSignUp = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      const encryptedPassword = await encryptMessageWithRSAOAEP(
        form.password,
        PUBLIC_KEY_PEM
      );

      // Combine firstName and lastName into fullName for API
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      
      // Generate unique connection ID
      const connectionId = generateId();
      let result: any = {};

      const requestData = {
        email: form.email,
        fullName,
        firstName: form.firstName,
        lastName: form.lastName,
        password: encryptedPassword,
        iSReceiveCompliance: form.ComplianceUpdates ? 1 : 0,
        connectionId,
      };
      if (clientType === 'salesbot2290ai') {
        result = await zenworksServiceApi.signUp(requestData);
      } else {
        result = await zenworksServiceApi.idpSignUp(requestData);
      }

      if (result.success) {
        // Pass complete form data with connectionId to parent
        handelSignUp({ 
          ...result, 
          email: form.email, 
          content: {
            ...result.content, 
            connectionId,
            fullName,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
          } 
        });
      } else if (result.message === "OtpRequired") {
        // OTP is required for verification
        handelSignUp({ 
          ...result, 
          email: form.email, 
          content: {
            ...result.content, 
            connectionId,
            fullName,
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
          } 
        });
      } else {
        handelDisplayMessage({
          error: true,
          message: result?.message || "Something went wrong",
        });
      }
    } catch (error: any) {
      handelDisplayMessage({
        error: true,
        message: error.message,
      });
    } finally {
      setIsLoading(false);
      e.preventDefault();
    }
  };

  // const handleRecaptchaChange = (value: string | null) => {
  //   setForm({ ...form, captcha: value as string });
  // };
  const isPasswordValid = isValidPassword(form.password);
  const isPasswordMatch =
    form.confirmPassword.trim() !== '' &&
    form.password === form.confirmPassword;
  const isEmailValid = isValidEmail(form.email);
  
  const isFormValid =
    form.firstName.trim() !== '' &&
    form.lastName.trim() !== '' &&
    form.email.trim() !== '' &&
    isEmailValid &&
    form.password.trim() !== '' &&
    form.confirmPassword.trim() !== '' &&
    isPasswordValid &&
    isPasswordMatch &&
    form.tAndC;

  const clientType = process.env.REACT_APP_CLIENT;
  const termsLink = clientType === 'salesbot2290ai' 
    ? 'https://www.2290.ai/terms-conditions' 
    : 'https://www.tax1099.com/saas-terms-and-conditions';
  const privacyLink = clientType === 'salesbot2290ai'
    ? 'https://www.2290.ai/privacy-policy'
    : 'https://www.tax1099.com/privacy';

  // Color scheme based on client type
  const primaryColor = clientType === 'salesbot2290ai' ? '#16A34A' : '#114175'; // Green for 2290, Blue for Tax1099
  const hoverColor = clientType === 'salesbot2290ai' ? '#15803D' : '#6491C3'; // Darker green / Lighter blue
  const focusRingColor = clientType === 'salesbot2290ai' ? 'focus:ring-[#16A34A]' : 'focus:ring-[#114175]';
  const textColor = clientType === 'salesbot2290ai' ? 'text-[#16A34A]' : 'text-[#114175]';

  const handleLinkClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="p-4 sm:p-6 max-w-lg mx-auto w-full">
      {/* Title from AI */}
      {title && (
        <div className="text-xs text-left mb-3 sm:mb-4 text-gray-600 leading-relaxed">
          {title}
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900">Create Account for Free</h3>
  
      </div>

      {/* First Name and Last Name Row */}
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div className="relative">
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            name="firstName"
            type="text"
            id="firstName"
            className={`block py-2.5 px-3 w-full text-sm text-gray-900 bg-white border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 ${focusRingColor} focus:border-transparent`}
            value={form.firstName}
            onChange={(e) => {
              setForm({ ...form, firstName: e.target.value });
            }}
          />
        </div>
        <div className="relative">
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            name="lastName"
            type="text"
            id="lastName"
            className={`block py-2.5 px-3 w-full text-sm text-gray-900 bg-white border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 ${focusRingColor} focus:border-transparent`}
            value={form.lastName}
            onChange={(e) => {
              setForm({ ...form, lastName: e.target.value });
            }}
          />
        </div>
      </div>

      {/* Work Email */}
      <div className="mb-3 sm:mb-4">
        <label htmlFor="Email" className="block text-sm font-medium text-gray-700 mb-1">
          Work Email <span className="text-red-500">*</span>
        </label>
        <input
          name="email"
          type="email"
          id="Email"
          className={`block py-2.5 px-3 w-full text-sm text-gray-900 bg-white border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 ${focusRingColor} focus:border-transparent`}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        {form.email && !isValidEmail(form.email) && (
          <span className="text-xs text-red-600 mt-1">Invalid email.</span>
        )}
      </div>
      {/* Password */}
      <div className="mb-3 sm:mb-4">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password <span className="text-red-500">*</span>
        </label>
        <ShowPasswordInput
          name="password"
          id={'password'}
          label={''}
          className="relative"
          onChange={(e: any) => setForm({ ...form, password: e.target.value })}
          value={form.password}
          isPasswordValid={isPasswordValid}
          showPasswordRules
          focusRingColor={focusRingColor}
        />
      </div>

      {/* Confirm Password */}
      <div className="mb-4 sm:mb-6">
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
          Confirm Password <span className="text-red-500">*</span>
        </label>
        <ShowPasswordInput
          name="confirmPassword"
          id="confirmPassword"
          label={''}
          className="relative"
          onChange={(e: any) => setForm({ ...form, confirmPassword: e.target.value })}
          value={form.confirmPassword}
          isPasswordValid={isPasswordMatch}
          showPasswordRules={false}
          focusRingColor={focusRingColor}
        />
        {form.confirmPassword && !isPasswordMatch && (
          <span className="text-xs text-red-600 mt-1 block">Passwords do not match.</span>
        )}
      </div>
      {/* Get Started Button */}
      <ButtonWithLoading
        isLoading={isLoading}
        label="Get Started for Free"
        type="button"
        disabled={!isFormValid}
        className={`w-full py-3 rounded-full text-white font-semibold text-base transition-colors ${
          isFormValid 
            ? `cursor-pointer` 
            : 'bg-gray-300 cursor-not-allowed'
        }`}
        style={isFormValid ? {
          backgroundColor: primaryColor,
        } : {}}
        onMouseEnter={(e: any) => {
          if (isFormValid) {
            e.currentTarget.style.backgroundColor = hoverColor;
          }
        }}
        onMouseLeave={(e: any) => {
          if (isFormValid) {
            e.currentTarget.style.backgroundColor = primaryColor;
          }
        }}
        onClick={handleSignUp}
      />

      {/* Display Error/Success Message */}
      {displayMessage.message && (
        <DisplayInfoMessage displayMessage={displayMessage} />
      )}

      {/* Or Sign Up With */}
      {(GOOGLE_AUTH_ENABLED || APPLE_AUTH_ENABLED || MSAL_ENABLED) && (
        <>
          {/* <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or Sign Up with</span>
            </div>
          </div> */}

          {/* Social Auth Buttons */}
          {/* <div className="grid grid-cols-2 gap-3">
            {GOOGLE_AUTH_ENABLED && (
              <button className="flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </button>
            )}
            {APPLE_AUTH_ENABLED && (
              <button className="flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              </button>
            )}
          </div> */}
        </>
      )}

      {/* Terms and Privacy Policy */}
      <div className="mt-4 sm:mt-6 text-xs text-center text-gray-600 leading-relaxed px-2">
        By creating an account, you agree to our{' '}
        <button 
          onClick={() => handleLinkClick(termsLink)}
          className={`${textColor} hover:underline font-medium`}
        >
          Terms and Conditions
        </button>
        .{' '}
        You also agree to receive emails regarding your tax return status, IRS notifications, and upcoming deadlines from Tax1099. Read our{' '}
        <button 
          onClick={() => handleLinkClick(privacyLink)}
          className={`${textColor} hover:underline font-medium`}
        >
          Privacy Policy
        </button>
        .
      </div>
    </div>
  );
};

export default SignUp;
